import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Modal } from "../../Shared/ScreenComponents.jsx";
import "../../Styles/DeactivateScreen.css";

import { apiPost } from "../../Api/Client.js";
import { formatDateTime, toggleSet } from "../../Shared/ScreenUtils.jsx";
import { allSelected, selectableIds } from "./deactivateSelection.js";

// Tenure, not a literal birthday — there's no date-of-birth field anywhere
// in this system, and "how long has this account existed" is the reading
// that actually matters on a page about deciding whether to deactivate one.
// Sort value is tenure length in days (bigger = longer-serving), so "Age"
// sorts the same direction a person would expect: ascending puts the
// newest joiners first, descending puts the longest-tenured first.
function tenureDays(joinedOn) {
  if (!joinedOn) return 0;
  const start = new Date(joinedOn);
  if (Number.isNaN(start.getTime())) return 0;
  return Math.max(0, Math.floor((Date.now() - start.getTime()) / (1000 * 60 * 60 * 24)));
}

function tenureLabel(joinedOn) {
  const days = tenureDays(joinedOn);
  if (!joinedOn) return "—";
  if (days < 30) return `${days}d`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}m`;
  const years = Math.floor(months / 12);
  const remMonths = months % 12;
  return remMonths > 0 ? `${years}y ${remMonths}m` : `${years}y`;
}

export function DeactivateEmployeeScreen({ data, reload }) {
  const [departmentId, setDepartmentId] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  const departmentNameById = useMemo(
    () => new Map((data.departments || []).map((department) => [String(department.id), department.name])),
    [data.departments],
  );

  const filteredEmployees = (data.employees || []).filter((employee) => !departmentId || String(employee.department) === String(departmentId));

  // One column config drives both the header (label + click-to-sort) and
  // the comparator — a column can never sort by a value different from what
  // it displays, because both read from the exact same `value` function.
  const columns = useMemo(() => [
    { key: "name", label: "User Name", value: (e) => (e.username || e.display_name || "").toLowerCase() },
    { key: "last_login", label: "Last Login", value: (e) => new Date(e.profile_payload?.last_login || e.updated_at || 0).getTime() },
    { key: "status", label: "Status", value: (e) => e.status || "" },
    { key: "age", label: "Age", value: (e) => tenureDays(e.joined_on) },
    { key: "department", label: "Department", value: (e) => (departmentNameById.get(String(e.department)) || "").toLowerCase() },
  ], [departmentNameById]);

  const employees = useMemo(() => {
    const column = columns.find((c) => c.key === sortKey) || columns[0];
    const sorted = [...filteredEmployees].sort((a, b) => {
      const va = column.value(a);
      const vb = column.value(b);
      if (va < vb) return -1;
      if (va > vb) return 1;
      return 0;
    });
    if (sortDir === "desc") sorted.reverse();
    return sorted;
  }, [filteredEmployees, columns, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const deactivateOne = async () => {
    if (!confirmTarget) return;
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      await apiPost("/MainApp/deactivate-employee/", { employee_id: confirmTarget, reason });
      setConfirmOpen(false);
      setConfirmTarget(null);
      setReason("");
      setSuccess("Employee Deactivated Successfully.");
      reload();
    } catch (err) {
      setError(err?.payload?.detail || err?.message || "Failed To Deactivate Employee.");
    } finally {
      setBusy(false);
    }
  };

  const deactivateSelected = async () => {
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      await apiPost("/MainApp/deactivate-multiple-employee/", { employee_ids: Array.from(selected), reason });
      setSelected(new Set());
      setBulkConfirmOpen(false);
      setReason("");
      setSuccess(`${selected.size} Employee(s) Deactivated Successfully.`);
      reload();
    } catch (err) {
      setError(err?.payload?.detail || err?.message || "Failed To Deactivate Selected Employees.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="Deactivate-Page">
      <h1>Deactivate Employee</h1>

      {success && <div className="Auth-AlertOk" style={{ marginBottom: 12 }}>{success}</div>}
      {error && <div className="Auth-Alert" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="Deactivate-Controls">
        <select value={departmentId} onChange={(event) => { setDepartmentId(event.target.value); setSelected(new Set()); }}>
          <option value="">All Departments</option>
          {(data.activeDepartments || []).map((department) => <option key={department.id} value={department.id}>{department.name}</option>)}
        </select>
        <span className="Deactivate-Count">{employees.length} {employees.length === 1 ? "user" : "users"}</span>
      </div>

      {selected.size > 0 && (
        <div style={{ marginBottom: 12 }}>
          <button className="Danger-Button" onClick={() => setBulkConfirmOpen(true)}>Deactivate Selected ({selected.size})</button>
        </div>
      )}

      <div className="Table-Scroll">
      <table className="Erp-TableStripedNarrow">
        <thead>
          <tr>
            <th><input type="checkbox" checked={allSelected(employees, selected)} onChange={(event) => setSelected(event.target.checked ? new Set(selectableIds(employees)) : new Set())} /></th>
            {columns.map((column) => (
              <th key={column.key}>
                <button type="button" className="Sortable-Col-Head" onClick={() => toggleSort(column.key)}>
                  {column.label}
                  {sortKey === column.key ? (sortDir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />) : null}
                </button>
              </th>
            ))}
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {employees.map((employee) => (
            <tr key={employee.id}>
              <td><input type="checkbox" checked={selected.has(employee.id)} disabled={employee.status === "Exited"} onChange={() => setSelected(toggleSet(selected, employee.id))} /></td>
              <td>{employee.username || employee.display_name}</td>
              <td>{formatDateTime(employee.profile_payload?.last_login || employee.updated_at)}</td>
              <td>{employee.status}</td>
              <td>{tenureLabel(employee.joined_on)}</td>
              <td>{departmentNameById.get(String(employee.department)) || "—"}</td>
              <td>
                {employee.status !== "Exited" && (
                  <button className="Warning-Button" onClick={() => { setConfirmTarget(employee.id); setConfirmOpen(true); }}>Deactivate</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {confirmOpen && (
        <Modal title="Confirm Deactivation" onClose={() => { setConfirmOpen(false); setConfirmTarget(null); setError(""); setSuccess(""); }}>
          <div style={{ padding: "8px 0" }}>
            <div style={{ background: "var(--red-50)", border: "1px solid var(--red-200)", borderRadius: 6, padding: "12px 16px", marginBottom: 16 }}>
              <strong style={{ color: "var(--red-600)", display: "block", marginBottom: 4 }}>Login Access Will Be Revoked</strong>
              <span style={{ fontSize: 13, color: "var(--red-700)" }}>
                This User Will No Longer Be Able To Log In To The Intranet. All Their Data (Projects, Tasks, Assignments, Documents, Feedback) Will Remain Intact. This Action Can Be Reversed By An Admin.
              </span>
            </div>
            <label>Reason For Deactivation<textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Optional: Provide A Reason..." style={{ width: "100%", marginTop: 4 }} /></label>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button className="Danger-Button" onClick={deactivateOne} disabled={busy}>Confirm Deactivation</button>
            <button className="Soft-Button" onClick={() => { setConfirmOpen(false); setConfirmTarget(null); setError(""); }}>Cancel</button>
          </div>
        </Modal>
      )}

      {bulkConfirmOpen && (
        <Modal title={`Deactivate ${selected.size} Employees`} onClose={() => { setBulkConfirmOpen(false); setError(""); setSuccess(""); }}>
          <div style={{ padding: "8px 0" }}>
            <div style={{ background: "var(--red-50)", border: "1px solid var(--red-200)", borderRadius: 6, padding: "12px 16px", marginBottom: 16 }}>
              <strong style={{ color: "var(--red-600)", display: "block", marginBottom: 4 }}>Login Access Will Be Revoked For {selected.size} Users</strong>
              <span style={{ fontSize: 13, color: "var(--red-700)" }}>
                These Users Will No Longer Be Able To Log In To The Intranet. All Their Data Will Remain Intact. This Action Can Be Reversed By An Admin.
              </span>
            </div>
            <label>Reason (Applies To All)<textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Optional: Provide A Reason..." style={{ width: "100%", marginTop: 4 }} /></label>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button className="Danger-Button" onClick={deactivateSelected} disabled={busy}>Confirm Deactivation</button>
            <button className="Soft-Button" onClick={() => { setBulkConfirmOpen(false); setError(""); }}>Cancel</button>
          </div>
        </Modal>
      )}
    </section>
  );
}
